import React from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

/**
 * Componente Accordion (Acordeón) con Headless UI + Tailwind
 * 
 * Reemplaza el Collapsible de Materialize
 * 
 * Uso:
 * <Accordion items={[
 *   { title: 'Sección 1', content: 'Contenido 1' },
 *   { title: 'Sección 2', content: <ComponenteCustom /> },
 * ]} />
 */
const Accordion = ({ 
  items = [], 
  allowMultiple = false,
  className = '' 
}) => {
  return (
    <div className={`space-y-2 ${className}`}>
      {items.map((item, index) => (
        <Disclosure key={index} defaultOpen={item.defaultOpen}>
          {({ open }) => (
            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              {/* Header del acordeón */}
              <DisclosureButton className="flex w-full items-center justify-between px-4 py-3 text-left text-gray-900 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                <span className="font-medium">{item.title}</span>
                <ChevronDownIcon
                  className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                    open ? 'rotate-180' : ''
                  }`}
                />
              </DisclosureButton>

              {/* Contenido con animación */}
              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <DisclosurePanel className="px-4 py-3 text-gray-600 border-t border-gray-200 bg-gray-50">
                  {item.content}
                </DisclosurePanel>
              </Transition>
            </div>
          )}
        </Disclosure>
      ))}
    </div>
  );
};

/**
 * Variante: Acordeón simple sin bordes
 */
export const AccordionSimple = ({ items = [], className = '' }) => {
  return (
    <div className={`divide-y divide-gray-200 ${className}`}>
      {items.map((item, index) => (
        <Disclosure key={index} defaultOpen={item.defaultOpen}>
          {({ open }) => (
            <div className="py-2">
              <DisclosureButton className="flex w-full items-center justify-between py-2 text-left text-gray-900 hover:text-blue-600 focus:outline-none">
                <span className="font-medium">{item.title}</span>
                <ChevronDownIcon
                  className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                    open ? 'rotate-180' : ''
                  }`}
                />
              </DisclosureButton>

              <DisclosurePanel className="pt-2 pb-4 text-gray-600">
                {item.content}
              </DisclosurePanel>
            </div>
          )}
        </Disclosure>
      ))}
    </div>
  );
};

export default Accordion;
